import './styles.scoped.css'
import {Fragment, useEffect, useState} from 'react'
import {Typography, Button, TextField, Link, Select, MenuItem, FormControl, FormHelperText, IconButton, Skeleton} from '@mui/material'
import {ArrowBack, UploadFile, Upload, Edit, Delete} from '@mui/icons-material'
import {useForm, Controller} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {useDropzone} from 'react-dropzone'
import {phoneRegex, emailRegex, usStates, parseError} from '../../utils/helpers'
import {useNavigate, useParams, Link as LinkRoute} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {adminApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {AddressAutocompleteField, SkeletonContainer, FieldSkeleton, ButtonSkeleton} from '../../components'

const emptyOrganization = () => ({
    name: '',
    type: '',
    email: '',
    phone: '',
    federal_tax_id: '',
    npi: '',
    tin: '',
    address: {street1: '', street2: '', city: '', state: '', zip: ''},
    contact_name: '',
    contact_phone: '',
    contact_email: '',
})

const Label = ({children, required}) => (
    <Typography variant="inputLabel" color="text.secondary">
        {children}
        {required && (
            <>
                {' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </>
        )}
    </Typography>
)

// The form laid out as it will be, labels and all, with placeholder inputs.
const FormSkeleton = ({mobile}) => {
    const fields = list =>
        list.map(([text, required]) => (
            <Fragment key={text}>
                <br />
                <br />
                <Label required={required}>{text}</Label>
                <FieldSkeleton label={false} />
            </Fragment>
        ))

    return (
        <SkeletonContainer>
            <Typography variant="overline" color="text.secondary">
                GENERAL INFORMATION
            </Typography>
            <br />
            <br />
            <div className="form" style={{display: mobile ? 'block' : 'flex'}}>
                <div className="form-left">
                    <Label>Healthcare Logo</Label>
                    {mobile ? (
                        <>
                            <br />
                            <ButtonSkeleton width={140} />
                            <br />
                        </>
                    ) : (
                        <Skeleton variant="rounded" height={150} sx={{minWidth: 300, borderRadius: '1px'}} />
                    )}
                </div>
                <div className="form-right">
                    <Label required>Healthcare Name</Label>
                    <FieldSkeleton label={false} />
                </div>
            </div>
            {fields([
                ['Organization Type', true],
                ['Healthcare Email'],
                ['Federal Tax ID (EIN)'],
                ['Organization NPI'],
                ['US Taxpayer ID (TIN)'],
                ['Healthcare Phone Number'],
            ])}
            <br />
            <br />
            <Typography variant="overline" color="text.secondary">
                ADDRESS
            </Typography>
            {fields([['Street Address Line 1', true], ['Street Address Line 2']])}
            <br />
            <br />
            <div className="form">
                <div className="form-left">
                    <Label required>City</Label>
                    <FieldSkeleton label={false} />
                </div>
                <div className="form-right">
                    <Label required>State</Label>
                    <FieldSkeleton label={false} />
                </div>
            </div>
            {fields([['Zip Code', true]])}
            <br />
            <br />
            <br />
            <Typography variant="overline" color="text.secondary">
                CONTACT PERSON
            </Typography>
            {fields([['Name'], ['Contact Person Phone Number'], ['Contact Person Email']])}
        </SkeletonContainer>
    )
}

// Editing an organization's own details. Adding one is its own page,
// AdminOrganizationAdd, which walks through branches, EVV and users as well.
export default () => {
    const {organizationId} = useParams()
    const {mobile} = useMobile()
    const navigate = useNavigate()
    const {setLoading, setMessageBox} = useGlobalStates()
    const form = useForm({
        defaultValues: emptyOrganization(),
        resolver: yupResolver(
            yup.object().shape({
                name: yup.string().required('Healthcare name is required'),
                type: yup.string().required('Organization type is required'),
                email: yup.string().test('email-optional', 'Invalid email address', value => !value || emailRegex.test(value)),
                phone: yup.string().test('phone-optional', 'Invalid phone number', value => !value || phoneRegex.test(value)),
                federal_tax_id: yup.string(),
                npi: yup
                    .string()
                    .trim()
                    .test('npi', 'Organization NPI must be 10 digits', value => !value || /^\d{10}$/.test(value)),
                tin: yup
                    .string()
                    .trim()
                    .test('tin', 'US Taxpayer ID must be 9 digits', value => !value || /^\d{9}$/.test(value)),
                address: yup.object().shape({
                    street1: yup.string().required('Street is required'),
                    street2: yup.string(),
                    city: yup.string().required('City is required'),
                    state: yup.string().required('State is required'),
                    zip: yup.string().required('ZIP code is required'),
                }),
                contact_name: yup.string(),
                contact_phone: yup.string().test('phone-optional', 'Invalid phone number', value => !value || phoneRegex.test(value)),
                contact_email: yup.string().test('email-optional', 'Invalid email address', value => !value || emailRegex.test(value)),
            }),
        ),
    })
    const [skeleton, setSkeleton] = useState(true)
    const [logoUrl, setLogoUrl] = useState(null)
    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        accept: {'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg']},
        noClick: true,
        multiple: false,
        maxSize: 3 * 1024 * 1024,
        onDrop: acceptedFiles => {
            setLogoUrl(URL.createObjectURL(acceptedFiles[0]))
        },
    })

    useEffect(() => {
        setSkeleton(true)
        adminApi
            .getOrganization(organizationId)
            .then(({data: {data: org}}) => {
                setSkeleton(false)
                form.reset({
                    name: org?.name || '',
                    type: org?.type || '',
                    email: org?.email || '',
                    phone: org?.phone || '',
                    federal_tax_id: org?.federal_tax_id || '',
                    npi: org?.npi || '',
                    tin: org?.tin || '',
                    address: {
                        street1: org?.address?.street || '',
                        street2: '',
                        city: org?.address?.city || '',
                        state: org?.address?.state || '',
                        zip: org?.address?.zip || '',
                    },
                    contact_name: org?.contact_name || '',
                    contact_phone: org?.contact_phone || '',
                    contact_email: org?.contact_email || '',
                })
            })
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [organizationId])

    const removeFile = () => {
        setLogoUrl(null)
    }

    const selectAddress = selected => {
        form.setValue('address.street2', '')
        form.setValue('address.city', selected?.city)
        form.setValue('address.state', selected?.state)
        form.setValue('address.zip', selected?.postcode)
    }

    const cancel = () => navigate(`/admin/organizations/${organizationId}`)

    const submit = data => {
        const payload = {
            name: data.name,
            type: data.type,
            email: data.email,
            phone: data.phone,
            federal_tax_id: data.federal_tax_id,
            // An empty one clears it.
            npi: data.npi,
            tin: data.tin,
            address: {street: data.address.street1, city: data.address.city, state: data.address.state, zip: data.address.zip},
            contact_name: data.contact_name,
            contact_phone: data.contact_phone,
            contact_email: data.contact_email,
        }

        setLoading(true)
        adminApi
            .updateOrganization(organizationId, payload)
            .then(() => {
                setLoading(false)
                navigate(`/admin/organizations/${organizationId}`, {
                    state: {
                        toast: {
                            status: 'success',
                            title: 'Organization Profile Updated Successfully',
                            description: 'This organization profile has been updated successfully.',
                        },
                    },
                })
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                setMessageBox({
                    status: 'error',
                    title: title || 'Organization Update Failed',
                    description: description || 'There was an error updating this organization profile. Please try again later.',
                })
            })
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <IconButton onClick={cancel}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" color="text.primary">
                            Edit Organization Settings
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" onClick={cancel} style={{flex: 1}}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={form.handleSubmit(submit)} style={{flex: 1}}>
                            Save Changes
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    {/* Where this page sits: the list, the organization being
                        edited, then this page. It used to name the list twice —
                        once as "Organizations" and again as "Organization
                        Settings", both meaning /admin/organizations — and never
                        named the organization at all. */}
                    <Typography variant="body1" color="text.secondary">
                        <LinkRoute className="link" to="/admin/organizations">
                            Organizations
                        </LinkRoute>
                        {' / '}
                        <LinkRoute className="link" to={`/admin/organizations/${organizationId}`}>
                            {skeleton ? (
                                <Skeleton width={120} sx={{display: 'inline-block', verticalAlign: 'middle'}} />
                            ) : (
                                form.watch('name') || 'Organization'
                            )}
                        </LinkRoute>
                        {' / Edit'}
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <div className="back-button" onClick={cancel}>
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </div>
                            <Typography variant="h5" color="text.primary">
                                Edit Organization Settings
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button variant="outlined" onClick={cancel}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={form.handleSubmit(submit)}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            {skeleton && <FormSkeleton mobile={mobile} />}
            {/* Kept mounted while loading so the form can be filled once the organization arrives. */}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="overline" color="text.secondary">
                    GENERAL INFORMATION
                </Typography>
                <br />
                <br />
                <div className="form" style={{display: mobile ? 'block' : 'flex'}}>
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            Healthcare Logo
                        </Typography>
                        <div style={{display: logoUrl ? 'flex' : 'none', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                            <img src={logoUrl} height={102} />
                            <Button variant="outlined" startIcon={<Edit />} onClick={open}>
                                Replace
                            </Button>
                            <Button variant="outlined" startIcon={<Delete />} color="error" onClick={removeFile}>
                                Remove
                            </Button>
                        </div>
                        <div
                            {...getRootProps()}
                            style={{
                                width: '100%',
                                backgroundColor: isDragActive ? '#e3f2fd' : '#fbfbfb',
                                display: logoUrl || mobile ? 'none' : 'block',
                                borderColor: isDragActive ? '#3f51b5' : '#0000001F',
                                border: '1px dashed #0000001F',
                                borderRadius: 1,
                                height: 150,
                                minWidth: 300,
                                boxSizing: 'border-box',
                                textAlign: 'center',
                            }}
                        >
                            <input {...getInputProps()} />
                            <br />
                            <UploadFile style={{fontSize: 26}} color="primary" />
                            <Typography variant="body1" color="text.primary" style={{marginTop: 10, marginBottom: 10}}>
                                <Link underline="always" onClick={open} style={{cursor: 'pointer'}}>
                                    {'Click to upload '}
                                </Link>
                                or drag and drop
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                SVG, PNG, JPG or GIF (max. 3MB)
                            </Typography>
                        </div>
                        {!logoUrl && mobile && (
                            <>
                                <br />
                                <Button variant="outlined" startIcon={<Upload />} onClick={open}>
                                    Upload Logo
                                </Button>
                                <br />
                            </>
                        )}
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            Healthcare Name{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your healthcare name"
                            fullWidth
                            {...form.register('name')}
                            error={!!form.formState.errors?.name}
                            helperText={form.formState.errors?.name?.message}
                        />
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Organization Type{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="e.g. home_care or 245D"
                    fullWidth
                    {...form.register('type')}
                    error={!!form.formState.errors?.type}
                    helperText={form.formState.errors?.type?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Healthcare Email
                </Typography>
                <TextField
                    placeholder="Enter your healthcare email"
                    fullWidth
                    {...form.register('email')}
                    error={!!form.formState.errors?.email}
                    helperText={form.formState.errors?.email?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Federal Tax ID (EIN)
                </Typography>
                <TextField placeholder="Enter your federal tax ID" fullWidth {...form.register('federal_tax_id')} />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Organization NPI
                </Typography>
                <TextField
                    placeholder="Enter the organization NPI"
                    fullWidth
                    {...form.register('npi')}
                    error={!!form.formState.errors?.npi}
                    helperText={form.formState.errors?.npi?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    US Taxpayer ID (TIN)
                </Typography>
                <TextField
                    placeholder="Enter the US taxpayer ID"
                    fullWidth
                    {...form.register('tin')}
                    error={!!form.formState.errors?.tin}
                    helperText={form.formState.errors?.tin?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Healthcare Phone Number
                </Typography>
                <TextField
                    placeholder="Enter your healthcare phone number"
                    fullWidth
                    {...form.register('phone')}
                    error={!!form.formState.errors?.phone}
                    helperText={form.formState.errors?.phone?.message}
                />
                <br />
                <br />
                <Typography variant="overline" color="text.secondary">
                    ADDRESS
                </Typography>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 1{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <AddressAutocompleteField
                    control={form.control}
                    name="address.street1"
                    error={!!form.formState.errors?.address?.street1}
                    helperText={form.formState.errors?.address?.street1?.message}
                    onSelect={selectAddress}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 2
                </Typography>
                <TextField placeholder="Enter your street address line 2" fullWidth {...form.register('address.street2')} />
                <br />
                <br />
                <div className="form">
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            City{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your city"
                            fullWidth
                            {...form.register('address.city')}
                            error={!!form.formState.errors?.address?.city}
                            helperText={form.formState.errors?.address?.city?.message}
                        />
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            State{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <FormControl fullWidth error={!!form.formState.errors?.address?.state}>
                            <Controller
                                name="address.state"
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        displayEmpty
                                        renderValue={selected =>
                                            selected === '' ? <span style={{color: '#aaa'}}>Select your state</span> : selected
                                        }
                                        MenuProps={{
                                            PaperProps: {style: {maxHeight: 300}},
                                            anchorOrigin: {vertical: 'top', horizontal: 'left'},
                                            transformOrigin: {vertical: 'bottom', horizontal: 'left'},
                                        }}
                                    >
                                        {usStates.map(item => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{form.formState.errors?.address?.state?.message}</FormHelperText>
                        </FormControl>
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Zip Code{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter your zip code"
                    fullWidth
                    {...form.register('address.zip')}
                    error={!!form.formState.errors?.address?.zip}
                    helperText={form.formState.errors?.address?.zip?.message}
                />
                <br />
                <br />
                <br />
                <Typography variant="overline" color="text.secondary">
                    CONTACT PERSON
                </Typography>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Name
                </Typography>
                <TextField placeholder="Enter the contact person name" fullWidth {...form.register('contact_name')} />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Contact Person Phone Number
                </Typography>
                <TextField placeholder="Enter your contact person phone number" fullWidth {...form.register('contact_phone')} />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Contact Person Email
                </Typography>
                <TextField placeholder="Enter your email" fullWidth {...form.register('contact_email')} />
            </div>
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
